import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Mic, Brain, ShieldCheck, Utensils, Clock, DollarSign, AlertTriangle, CheckCircle2, TrendingUp, MessageSquare } from "lucide-react";

const timeline = [
  { 
    time: "6:00 AM", 
    title: "Supervisor Apollo Analyzes", 
    icon: Eye, 
    color: "lime",
    save: "Spoilage caught before service",
    impact: "Quality issue flagged before prep",
    desc: "Chef snaps a photo of the walk-in during morning prep and uploads it to Apollo. Apollo flags several items that won't survive the day and provides specific, actionable observations — before service starts.",
    supervisor: "APOLLO — Vision AI",
    metrics: [
      { label: "Photo analyzed", value: "< 10s" },
      { label: "Findings surfaced", value: "Actionable" },
      { label: "Upload-based", value: "V1" }
    ]
  },
  { 
    time: "8:00 AM", 
    title: "Supervisor Athena Documents", 
    icon: Brain, 
    color: "gold",
    save: "Voice recording → structured SOP",
    impact: "Operational knowledge captured and saved",
    desc: "Chef Marcus records his signature short rib preparation by voice. Athena converts it into a structured SOP with cost analysis — ready for the team within a minute. Ingredient costs calculated automatically.",
    supervisor: "ATHENA — SOP Builder",
    metrics: [
      { label: "Input method", value: "Voice" },
      { label: "Cost analysis", value: "Automatic" },
      { label: "Status", value: "V1 Live" }
    ]
  },
  { 
    time: "1:15 PM", 
    title: "Supervisor Echo Takes a Call", 
    icon: Utensils, 
    color: "purple-400",
    save: "Guest inquiry handled instantly",
    impact: "24/7 coverage without staff involvement",
    desc: "A guest calls about the menu and allergy options during the lunch rush. Echo handles the conversation naturally, answers questions about the menu and policies, and routes the call appropriately — no staff needed.",
    supervisor: "ECHO — AI Receptionist",
    metrics: [
      { label: "Availability", value: "24/7" },
      { label: "Languages", value: "Multilingual" },
      { label: "Status", value: "V1 Live" }
    ]
  },
  { 
    time: "5:30 PM", 
    title: "Supervisor Iris — Coming Soon", 
    icon: MessageSquare, 
    color: "pink-400",
    save: "Kitchen traffic control in development",
    impact: "Real-time task routing coming Q3 2026",
    desc: "Iris will become the kitchen's traffic controller — routing food-ready notifications to the right runner, escalating guest complaints to the manager, and balancing workload in real time. Coming Q3 2026.",
    supervisor: "IRIS — Kitchen Traffic (Coming Soon)",
    metrics: [
      { label: "Status", value: "Q3 2026" },
      { label: "KDS sync", value: "Coming" },
      { label: "Task routing", value: "In dev" }
    ]
  },
  { 
    time: "Future", 
    title: "Supervisor Atlas — Coming Soon", 
    icon: Mic, 
    color: "blue-400",
    save: "Staff coordination in development",
    impact: "BYOD, wearables, and staff app coming Q3 2026",
    desc: "Atlas will coordinate your entire team — sending alerts to staff devices, routing tasks to the right person, and guiding new hires step-by-step. Staff app and wearable support targeted for Q3 2026.",
    supervisor: "ATLAS — Staff Sync (Coming Soon)",
    metrics: [
      { label: "Status", value: "Q3 2026" },
      { label: "Staff app", value: "Coming" },
      { label: "Wearables", value: "Coming" }
    ]
  },
  { 
    time: "7:45 PM", 
    title: "The Council Convenes", 
    icon: ShieldCheck, 
    color: "gold",
    save: "Perfect service, zero chaos",
    impact: "Table turn time: -8 minutes avg",
    desc: "Dinner rush peaks at 127 covers. All five supervisors coordinate seamlessly — Apollo watches plating, Echo handles 6 simultaneous calls, Athena audits food safety, Atlas alerts servers, and Iris routes food runners to the right tables. Your restaurant runs on autopilot.",
    supervisor: "THE ARGUS COUNCIL",
    metrics: [
      { label: "Covers served", value: "127" },
      { label: "Issues flagged", value: "2" },
      { label: "Staff interventions", value: "0" }
    ]
  },
];

export const TimelineScroller = () => {
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const t = timeline[active];
  const Icon = t.icon;

  // Auto-advance every 8 seconds
  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % timeline.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  return (
    <section data-testid="timeline-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-lime">A Day in the Life</span>
          <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4">
            The Self-Driving <span className="text-gold">Restaurant</span>
          </h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">
            Watch how AI Supervisors work together to protect your margins, train your staff, and delight your guests — all day, every day.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Timeline nav */}
          <div className="lg:col-span-4">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-0 bottom-0 w-px bg-indigo-500" />
              <div className="space-y-2">
                {timeline.map((item, i) => (
                  <button key={i} data-testid={`timeline-item-${i}`} 
                    onClick={() => { setActive(i); setAutoPlay(false); }}
                    className={`relative flex items-center gap-4 w-full text-left p-4 rounded-lg transition-colors duration-300 ${
                      active === i ? "bg-indigo-500/30 border border-gold/20" : "hover:bg-indigo-500/10"
                    }`}>
                    <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                      active === i ? `border-${item.color} bg-${item.color}/10` : "border-indigo-400 bg-indigo-700"
                    }`}>
                      <span className={`text-xs font-mono font-bold ${active === i ? `text-${item.color}` : "text-slate-500"}`}>
                        {item.time.split(" ")[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-mono ${active === i ? "text-gold" : "text-slate-500"}`}>{item.time}</span>
                      <p className={`text-sm font-inter font-semibold truncate ${active === i ? "text-white" : "text-slate-400"}`}>{item.title}</p>
                    </div>
                    {active === i && autoPlay && (
                      <div className="w-2 h-2 rounded-full bg-lime animate-pulse" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div key={active}
                className="glass-card rounded-xl p-8 md:p-10"
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35 }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-indigo-500/40 flex items-center justify-center border border-gold/20 animate-pulse-lime">
                    <Icon className={`w-6 h-6 text-${t.color}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-gold" />
                      <span className="text-sm font-mono text-gold font-bold">{t.time}</span>
                    </div>
                    <h3 className="font-inter text-2xl font-bold text-white">{t.title}</h3>
                  </div>
                </div>

                {/* Save callout */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-lime/5 border-l-4 border-lime rounded-r px-5 py-4">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-lime" />
                      <span className="font-inter font-black text-sm text-lime tracking-wider uppercase">THE SAVE</span>
                    </div>
                    <span className="text-sm text-white font-medium">{t.save}</span>
                  </div>
                  <div className="bg-gold/5 border-l-4 border-gold rounded-r px-5 py-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-gold" />
                      <span className="font-inter font-black text-sm text-gold tracking-wider uppercase">IMPACT</span>
                    </div>
                    <span className="text-sm text-white font-medium">{t.impact}</span>
                  </div>
                </div>

                <p className="text-slate-300 leading-relaxed mb-6">{t.desc}</p>

                {/* Metrics */}
                {t.metrics && (
                  <div className="flex flex-wrap gap-4 mb-6">
                    {t.metrics.map((m, i) => (
                      <div key={i} className="bg-indigo-700/30 rounded-lg px-4 py-2 border border-indigo-500/20">
                        <span className="text-xs text-slate-500 block">{m.label}</span>
                        <span className="text-sm font-bold text-white">{m.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-xs bg-gold/10 text-gold px-3 py-1 rounded font-inter font-bold tracking-wider">{t.supervisor}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
